import { defineBedrockEndpointContract } from "schemas/bedrockApi/contract";
import {
  findOAuthConnectedAppsOutputSchema,
  oauthClientVerificationSchema,
  revokeOAuthConnectedAppOutputSchema,
} from "schemas/publicMcpOAuth";
import { z } from "zod";

export const oauthRegisterBodySchema = z
  .object({
    client_name: z.string().min(1).max(255).optional(),
    redirect_uris: z.array(z.string()).min(1),
    token_endpoint_auth_method: z.literal("none").optional(),
    grant_types: z.array(z.string()).optional(),
    response_types: z.array(z.string()).optional(),
  })
  .passthrough();

export const oauthConsentBodySchema = z
  .object({
    clientId: z.string().min(1),
    codeChallenge: z.string().min(43).max(128),
    codeChallengeMethod: z.literal("S256"),
    redirectUri: z.string(),
    resource: z.string().optional(),
    state: z.string().max(2048).optional(),
  })
  .strict();

const oauthRegisterResponseSchema = z.object({
  client_id: z.string(),
  client_id_issued_at: z.number(),
  client_name: z.string(),
  redirect_uris: z.array(z.string()),
  token_endpoint_auth_method: z.literal("none"),
  grant_types: z.array(z.enum(["authorization_code", "refresh_token"])),
  response_types: z.array(z.literal("code")),
});

const oauthClientGetResponseSchema = z.object({
  data: z.object({
    id: z.string(),
    name: z.string(),
    redirectUris: z.array(z.string()),
    verification: oauthClientVerificationSchema,
  }),
});

const oauthConsentResponseSchema = z.object({
  data: z.object({
    redirectTo: z.string(),
  }),
});

const registerEndpoint = defineBedrockEndpointContract({
  operationId: "oauth.register",
  method: "post",
  path: "/api/v1/oauth/register",
  routePath: "/register",
  description: "Register an OAuth client",
  successStatus: 201,
  request: { body: oauthRegisterBodySchema },
  response: { type: "json", schema: oauthRegisterResponseSchema },
});

const clientGetEndpoint = defineBedrockEndpointContract({
  operationId: "oauth.clientGet",
  method: "get",
  path: "/api/v1/oauth/client/{clientId}",
  routePath: "/client/{clientId}",
  description: "Get OAuth client metadata",
  request: { params: z.object({ clientId: z.string().min(1) }) },
  response: { type: "json", schema: oauthClientGetResponseSchema },
});

const consentEndpoint = defineBedrockEndpointContract({
  operationId: "oauth.consent",
  method: "post",
  path: "/api/v1/oauth/manage/consent",
  routePath: "/consent",
  description: "Approve an OAuth authorization request",
  request: { body: oauthConsentBodySchema },
  response: { type: "json", schema: oauthConsentResponseSchema },
});

const connectedAppsListEndpoint = defineBedrockEndpointContract({
  operationId: "oauth.connectedAppsList",
  method: "get",
  path: "/api/v1/oauth/manage/grants",
  routePath: "/grants",
  description: "List OAuth apps connected to the current account",
  request: { query: z.object({ cursor: z.string().optional() }) },
  response: {
    type: "json",
    schema: z.object({ data: findOAuthConnectedAppsOutputSchema }),
  },
});

const connectedAppRevokeEndpoint = defineBedrockEndpointContract({
  operationId: "oauth.connectedAppRevoke",
  method: "delete",
  path: "/api/v1/oauth/manage/grants/{grantId}",
  routePath: "/grants/{grantId}",
  description: "Revoke an OAuth app connected to the current account",
  request: { params: z.object({ grantId: z.string().min(1) }) },
  response: {
    type: "json",
    schema: z.object({ data: revokeOAuthConnectedAppOutputSchema }),
  },
});

export const oauthEndpointContracts = {
  register: registerEndpoint,
  clientGet: clientGetEndpoint,
  consent: consentEndpoint,
  connectedAppsList: connectedAppsListEndpoint,
  connectedAppRevoke: connectedAppRevokeEndpoint,
} as const;
